<template>
  <div class="viz-root relative w-full">
    <div v-if="!points || points.length === 0" class="text-sm text-gray-400 py-8 text-center">
      No data available
    </div>
    <svg
      v-else
      :viewBox="`0 0 ${W} ${H}`"
      width="100%"
      :height="H"
      role="img"
      :aria-label="ariaLabel"
      @mousemove="onMove"
      @mouseleave="hovered = -1"
    >
      <!-- gridlines + y ticks -->
      <g v-for="t in yTicks" :key="'y' + t.v">
        <line :x1="padL" :y1="t.y" :x2="W - padR" :y2="t.y" stroke="#e1e0d9" stroke-width="1" />
        <text :x="padL - 6" :y="t.y + 3" text-anchor="end" font-size="10" fill="#898781"
          style="font-family: system-ui, sans-serif; font-variant-numeric: tabular-nums;">{{ shortFmt(t.v) }}</text>
      </g>
      <!-- x labels -->
      <text v-for="(p, i) in coords" :key="'x' + i" :x="p.x" :y="H - 6"
        text-anchor="middle" font-size="10" fill="#898781"
        style="font-family: system-ui, sans-serif;">{{ shortMonth(p.label) }}</text>

      <!-- area + line -->
      <path :d="areaPath" fill="#2a78d6" opacity="0.08" />
      <path :d="linePath" fill="none" stroke="#2a78d6" stroke-width="2"
        stroke-linejoin="round" stroke-linecap="round" />
      <circle v-for="(p, i) in coords" :key="'m' + i" :cx="p.x" :cy="p.y" :r="hovered === i ? 5 : 4"
        fill="#2a78d6" stroke="#fff" stroke-width="2" />

      <!-- crosshair -->
      <line v-if="hovered >= 0" :x1="coords[hovered].x" :y1="padT" :x2="coords[hovered].x" :y2="H - padB"
        stroke="#c3c2b7" stroke-width="1" stroke-dasharray="3 3" />
    </svg>

    <div
      v-if="hovered >= 0"
      class="pointer-events-none absolute z-10 rounded-md bg-[#0b0b0b] text-white text-xs px-2 py-1 shadow-lg"
      :style="{ left: tip.x + 'px', top: tip.y + 'px', transform: 'translate(-50%, -130%)' }"
    >
      <div class="font-medium">{{ points[hovered].label }}</div>
      <div class="tabular-nums">{{ format(points[hovered].value) }}</div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

// Single-series time line. Same y-scale only (no dual axis).
export default {
  props: {
    points: { type: Array, default: () => [] }, // [{ label: 'YYYY-MM', value }]
    format: { type: Function, default: (v) => v.toLocaleString() },
    ariaLabel: { type: String, default: "Trend line" },
  },
  setup(props) {
    const W = 640, H = 240, padL = 56, padR = 16, padT = 12, padB = 24;
    const hovered = ref(-1);
    const tip = ref({ x: 0, y: 0 });

    const maxVal = computed(() => Math.max(1, ...props.points.map((p) => p.value || 0)));
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const coords = computed(() => {
      const n = props.points.length;
      return props.points.map((p, i) => ({
        label: p.label,
        value: p.value || 0,
        x: n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW,
        y: padT + plotH - ((p.value || 0) / maxVal.value) * plotH,
      }));
    });

    const linePath = computed(() =>
      coords.value.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ")
    );
    const areaPath = computed(() => {
      if (coords.value.length === 0) return "";
      const first = coords.value[0], last = coords.value[coords.value.length - 1];
      return `${linePath.value} L${last.x},${padT + plotH} L${first.x},${padT + plotH} Z`;
    });

    const yTicks = computed(() =>
      [0, 0.5, 1].map((f) => ({ v: maxVal.value * f, y: padT + plotH - f * plotH }))
    );

    const onMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * W;
      let best = 0, bestD = Infinity;
      coords.value.forEach((p, i) => {
        const d = Math.abs(p.x - x);
        if (d < bestD) { bestD = d; best = i; }
      });
      hovered.value = best;
      tip.value = {
        x: (coords.value[best].x / W) * rect.width,
        y: (coords.value[best].y / H) * rect.height,
      };
    };

    const shortFmt = (v) => {
      if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
      if (v >= 1e3) return Math.round(v / 1e3) + "k";
      return Math.round(v).toString();
    };
    const shortMonth = (ym) => {
      const [y, m] = (ym || "").split("-");
      const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return m ? `${names[parseInt(m, 10)]} ${(y || "").slice(2)}` : ym;
    };

    return { W, H, padL, padR, padT, padB, coords, linePath, areaPath, yTicks, hovered, tip, onMove, shortFmt, shortMonth };
  },
};
</script>
